'use client';

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Building2, CheckCircle, ClipboardCheck, FileCheck, FileText, Loader2 } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import type { OrgTreeNode } from '@/api/organizations';
import { GroupScopePill } from '@/components/group-scope-pill';
import { PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, type SelectOption } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCalculateConsolidation, useConsolidations } from '@/features/ghg/api/use-consolidation';
import { useGHGReports } from '@/features/ghg/api/use-ghg-reports';
import { useVerificationChecklists } from '@/features/ghg/api/use-verification-checklist';
import { GHGReportDialog, ISO14064ReportDialog } from '@/features/ghg/components';
import {
  ReportPipelineProgress,
  type PipelineStageStatus,
} from '@/features/ghg/components/ReportPipelineProgress';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import {
  DEFAULT_GHG_REPORT_SCOPE,
  GhgReportScopePicker,
  resolveGhgScopeRequest,
  type GhgReportScope,
} from '@/features/ghg/reports';
import { useOrgTree } from '@/features/settings/subsidiaries/api/use-org-tree';
import i18n from '@/lib/i18n';

const reportSearchSchema = z.object({
  tab: z.enum(['consolidate', 'verify', 'report']).default('consolidate').catch('consolidate'),
});

interface FlatOrg {
  id: string;
  aggregatedReportingEnabled: boolean;
}

function flattenOrgTree(node: OrgTreeNode, out: FlatOrg[] = []): FlatOrg[] {
  out.push({ id: node.id, aggregatedReportingEnabled: node.aggregatedReportingEnabled });
  for (const child of node.children) {
    flattenOrgTree(child, out);
  }
  return out;
}

export const Route = createFileRoute('/_authenticated/ghg/report')({
  validateSearch: reportSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.reportVerify'),
  }),
  component: ReportAndVerifyPage,
});

function ReportAndVerifyPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const { periodId } = useGHGPagePeriod();

  // WP3-H: document-level scope controls whether the consolidation
  // calculation and downstream report + verification actions operate
  // parent-only, across every eligible subsidiary, or across an
  // explicit subset.
  const [scope, setScope] = React.useState<GhgReportScope>(DEFAULT_GHG_REPORT_SCOPE);
  const scopeRequest = React.useMemo(() => resolveGhgScopeRequest(scope), [scope]);
  const isConsolidatedScope =
    scopeRequest.includeSubsidiaries ||
    (scopeRequest.explicitOrgIds?.length ?? 0) > 0;

  // Consolidation data (scope-aware: a fresh cache entry is spawned
  // whenever the scope selector changes)
  const { inventories, isLoading: inventoriesLoading } = useConsolidations({}, scope);
  const calculateConsolidation = useCalculateConsolidation(scope);

  // Org tree for entity selection
  const { data: orgTreeData } = useOrgTree();
  const [excludedEntityIds, setExcludedEntityIds] = React.useState<Set<string>>(new Set());

  // Synthetic rollup metadata for the group scope pill — derived from
  // the selected scope + org tree. When the backend returns real
  // ``scope_metadata`` on inventory responses we can swap this for the
  // live payload.
  const groupScopePillMetadata = React.useMemo(() => {
    if (!isConsolidatedScope) return undefined;
    // Skip the root org (implicit parent) and keep only descendants
    // with group reporting enabled.
    const allDescendants = orgTreeData?.tree
      ? flattenOrgTree(orgTreeData.tree)
          .slice(1)
          .filter((n) => n.aggregatedReportingEnabled)
      : [];
    const includedIds =
      scope.mode === 'explicit'
        ? (scope.explicitOrgIds ?? [])
        : allDescendants.map((n) => n.id);
    return {
      includeSubsidiaries: true,
      // +1 for the parent org that's always included implicitly
      includedOrganizationCount: includedIds.length + 1,
      includedOrganizationIds: [
        ...(orgTreeData?.tree ? [orgTreeData.tree.id] : []),
        ...includedIds,
      ],
    };
  }, [isConsolidatedScope, scope, orgTreeData]);

  // Verification data
  const { checklists, isLoading: checklistsLoading } = useVerificationChecklists();

  // Pipeline status
  const hasInventory = inventories.length > 0;
  const hasVerification = checklists.some(
    (c: { status: string }) => c.status === 'completed'
  );

  const consolidateStatus: PipelineStageStatus = hasInventory ? 'complete' : 'in-progress';
  const verifyStatus: PipelineStageStatus = hasInventory
    ? hasVerification
      ? 'complete'
      : 'in-progress'
    : 'locked';
  const reportStatus: PipelineStageStatus = hasInventory
    ? hasVerification
      ? 'complete'
      : 'in-progress'
    : 'locked';

  const handleTabChange = (value: string) => {
    void navigate({
      to: '/ghg/report',
      search: { tab: value as 'consolidate' | 'verify' | 'report' },
      replace: true,
    });
  };

  const handleCalculateInventory = async () => {
    if (!periodId) {
      toast.error(t('toast.noReportingPeriodSelectedCompleteGHGSetupFirst'), {
        action: {
          label: t('reportVerify.goToSetup'),
          onClick: () => void navigate({ to: '/ghg/setup' }),
        },
      });
      return;
    }
    try {
      await calculateConsolidation.mutateAsync({
        reportingPeriodId: periodId,
        recalculate: true,
        ...(excludedEntityIds.size > 0 && {
          excludedEntityIds: Array.from(excludedEntityIds),
        }),
      });
      toast.success(t('toast.inventoryCalculatedSuccessfully'));
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('reportVerify.failedToStartCalculation');
      toast.error(message, {
        description: t('reportVerify.ensureActivityData'),
        action: {
          label: t('reportVerify.goToSetup'),
          onClick: () => void navigate({ to: '/ghg/setup' }),
        },
      });
    }
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <PageHeader
          title={t('reportVerify.pageTitle')}
          description={t('reportVerify.pageDescription')}
          module="ghg"
          icon={<FileCheck className="h-6 w-6" />}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <GhgReportScopePicker
            value={scope}
            onChange={setScope}
            triggerLabelPrefix={t('reportVerify.scopePickerLabel', {
              defaultValue: 'Scope',
            })}
          />
        </div>

        <GroupScopePill metadata={groupScopePillMetadata} />

        <ReportPipelineProgress
          stages={[
            { label: t('reportVerify.stage1'), status: consolidateStatus },
            { label: t('reportVerify.stage2'), status: verifyStatus },
            { label: t('reportVerify.stage3'), status: reportStatus },
          ]}
        />

        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="consolidate" className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" />
              {t('reportVerify.tabConsolidate')}
            </TabsTrigger>
            <TabsTrigger value="verify" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              {t('reportVerify.tabVerify')}
            </TabsTrigger>
            <TabsTrigger value="report" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('reportVerify.tabReport')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="consolidate" className="space-y-6">
            {orgTreeData?.tree && orgTreeData.tree.children.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Building2 className="h-4 w-4" />
                    {t('reportVerify.entitySelection', 'Entities to Consolidate')}
                  </CardTitle>
                  <CardDescription>
                    {t(
                      'reportVerify.entitySelectionDesc',
                      'Select which organizations to include in the consolidated inventory.'
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <EntityCheckbox
                      node={orgTreeData.tree}
                      depth={0}
                      excludedIds={excludedEntityIds}
                      onToggle={(id) => {
                        setExcludedEntityIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(id)) {
                            next.delete(id);
                          } else {
                            next.add(id);
                          }
                          return next;
                        });
                      }}
                      isRoot
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('reportVerify.consolidatedInventories')}</CardTitle>
                    <CardDescription>
                      {t('reportVerify.consolidatedInventoriesDesc')}
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => void handleCalculateInventory()}
                    disabled={calculateConsolidation.isPending || !periodId}
                  >
                    {calculateConsolidation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {t('reportVerify.calculateInventory')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {inventoriesLoading ? (
                  <Skeleton className="h-32 w-full" />
                ) : inventories.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    {t('reportVerify.noInventoriesYet')}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('reportVerify.thInventory')}</TableHead>
                        <TableHead>{t('reportVerify.thStatus')}</TableHead>
                        <TableHead>{t('reportVerify.thFacilities')}</TableHead>
                        <TableHead>{t('reportVerify.thTotalTCO2e')}</TableHead>
                        <TableHead>{t('reportVerify.thCalculated')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inventories.map(
                        (inv: {
                          id: string;
                          year?: number;
                          consolidationApproach?: string;
                          status?: string;
                          totalEmissions?: number;
                          facilitiesIncluded?: number;
                          calculatedAt?: string;
                        }) => (
                          <TableRow key={inv.id}>
                            <TableCell className="font-medium">
                              {inv.year ?? '—'}{' '}
                              <span className="text-xs text-muted-foreground">
                                {inv.consolidationApproach?.replace(/_/g, ' ') ?? ''}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant={inv.status === 'locked' ? 'default' : 'secondary'}>
                                {inv.status ?? t('reportVerify.calculated')}
                              </Badge>
                            </TableCell>
                            <TableCell>{inv.facilitiesIncluded ?? '—'}</TableCell>
                            <TableCell>
                              {inv.totalEmissions != null
                                ? inv.totalEmissions.toLocaleString()
                                : '\u2014'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {inv.calculatedAt
                                ? new Date(inv.calculatedAt).toLocaleDateString()
                                : '\u2014'}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="verify" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('reportVerify.verificationChecklists')}</CardTitle>
                    <CardDescription>
                      {t('reportVerify.verificationChecklistsDesc')}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/ghg/verification/internal">{t('reportVerify.internalVerification')}</Link>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {checklistsLoading ? (
                  <Skeleton className="h-32 w-full" />
                ) : checklists.length === 0 ? (
                  <div className="py-8 text-center">
                    <CheckCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                    <h3 className="text-lg font-medium">{t('reportVerify.noVerificationsYet')}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('reportVerify.startVerificationDesc')}
                    </p>
                    <Button className="mt-4" asChild>
                      <Link to="/ghg/verification/internal">{t('reportVerify.startVerification')}</Link>
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('reportVerify.thChecklist')}</TableHead>
                        <TableHead>{t('reportVerify.thStatus')}</TableHead>
                        <TableHead>{t('reportVerify.thProgress')}</TableHead>
                        <TableHead>{t('reportVerify.thAssurance')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {checklists.map(
                        (checklist: {
                          id: string;
                          title?: string;
                          status?: string;
                          completionPercentage?: number;
                          assuranceLevel?: string;
                        }) => (
                          <TableRow key={checklist.id}>
                            <TableCell>
                              <Link
                                to="/ghg/verification/$checklistId"
                                params={{ checklistId: checklist.id }}
                                className="font-medium text-primary hover:underline"
                              >
                                {checklist.title ?? t('reportVerify.untitled')}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  checklist.status === 'completed' ? 'default' : 'secondary'
                                }
                              >
                                {checklist.status ?? t('reportVerify.draft')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Progress
                                  value={checklist.completionPercentage ?? 0}
                                  className="h-2 w-20"
                                />
                                <span className="text-xs text-muted-foreground">
                                  {Math.round(checklist.completionPercentage ?? 0)}%
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {checklist.assuranceLevel ?? '\u2014'}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="report" className="space-y-6">
            {!hasInventory ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                  <h3 className="text-lg font-medium">{t('reportVerify.noInventoryAvailable')}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('reportVerify.consolidateFirst')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <ReportsTabContent scope={scope} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ReportsTabContent({ scope }: { scope: GhgReportScope }) {
  const { t } = useTranslation('ghg');
  const [selectedInventoryId, setSelectedInventoryId] = React.useState<string>('');
  const [ghgDialogOpen, setGhgDialogOpen] = React.useState(false);
  const [isoDialogOpen, setIsoDialogOpen] = React.useState(false);

  const scopeRequest = React.useMemo(() => resolveGhgScopeRequest(scope), [scope]);
  const isConsolidatedScope =
    scopeRequest.includeSubsidiaries ||
    (scopeRequest.explicitOrgIds?.length ?? 0) > 0;

  const { inventories, isLoading: inventoriesLoading } = useConsolidations({}, scope);
  const { reports } = useGHGReports({}, scope);

  const inventoryOptions: SelectOption[] = React.useMemo(
    () =>
      inventories.map(
        (inv: { id: string; year?: number; consolidationApproach?: string; status?: string }) => ({
          value: inv.id,
          label: `${String(inv.year ?? 'Unknown')} — ${inv.consolidationApproach?.replace(/_/g, ' ') ?? ''} (${inv.status ?? 'calculated'})`,
        })
      ),
    [inventories]
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('reportVerify.generateReports')}</CardTitle>
          <CardDescription>
            {t('reportVerify.generateReportsDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {inventoriesLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : inventoryOptions.length > 0 ? (
            <>
              <div className="space-y-2">
                <Label>{t('reportVerify.selectInventory')}</Label>
                <Select
                  options={inventoryOptions}
                  value={selectedInventoryId}
                  onChange={setSelectedInventoryId}
                  placeholder={t('reportVerify.selectAnInventory')}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => { setGhgDialogOpen(true); }}
                  disabled={!selectedInventoryId}
                  data-testid="generate-ghg-protocol-report"
                >
                  <FileCheck className="mr-2 h-4 w-4" />
                  {t('reportVerify.ghgProtocolReport')}
                  {isConsolidatedScope && (
                    <Badge
                      variant="outline"
                      className="ml-2 border-emerald-300 bg-emerald-50 text-[10px] text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200"
                      data-testid="download-consolidated-badge-ghg"
                    >
                      {t('reportVerify.consolidated', { defaultValue: 'Consolidated' })}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setIsoDialogOpen(true); }}
                  disabled={!selectedInventoryId}
                  data-testid="generate-iso-14064-report"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  {t('reportVerify.iso14064Report')}
                  {isConsolidatedScope && (
                    <Badge
                      variant="outline"
                      className="ml-2 border-emerald-300 bg-emerald-50 text-[10px] text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200"
                      data-testid="download-consolidated-badge-iso"
                    >
                      {t('reportVerify.consolidated', { defaultValue: 'Consolidated' })}
                    </Badge>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('reportVerify.noInventoriesAvailable')}
            </p>
          )}
        </CardContent>
      </Card>

      {reports.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('reportVerify.generatedReports')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('reportVerify.thReport')}</TableHead>
                  <TableHead>{t('reportVerify.thType')}</TableHead>
                  <TableHead>{t('reportVerify.thStatus')}</TableHead>
                  <TableHead>{t('reportVerify.thCreated')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map(
                  (report: {
                    id: string;
                    title?: string;
                    reportType?: string;
                    status?: string;
                    createdAt?: string;
                  }) => (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">{report.title ?? report.id}</TableCell>
                      <TableCell>{report.reportType ?? '\u2014'}</TableCell>
                      <TableCell>
                        <Badge variant={report.status === 'final' ? 'default' : 'secondary'}>
                          {report.status ?? t('reportVerify.draft')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {report.createdAt
                          ? new Date(report.createdAt).toLocaleDateString()
                          : '\u2014'}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {ghgDialogOpen && selectedInventoryId && (
        <GHGReportDialog
          inventoryId={selectedInventoryId}
          open={ghgDialogOpen}
          onOpenChange={setGhgDialogOpen}
          scope={scope}
        />
      )}
      {isoDialogOpen && selectedInventoryId && (
        <ISO14064ReportDialog
          inventoryId={selectedInventoryId}
          open={isoDialogOpen}
          onOpenChange={setIsoDialogOpen}
          scope={scope}
        />
      )}
    </>
  );
}

function EntityCheckbox({
  node,
  depth,
  excludedIds,
  onToggle,
  isRoot = false,
}: {
  node: OrgTreeNode;
  depth: number;
  excludedIds: Set<string>;
  onToggle: (id: string) => void;
  isRoot?: boolean;
}) {
  const checked = !excludedIds.has(node.id);

  return (
    <>
      <div
        className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/50"
        style={{ paddingLeft: `${depth * 24 + 8}px` }}
      >
        <Checkbox
          id={`entity-${node.id}`}
          checked={checked}
          onCheckedChange={() => { onToggle(node.id); }}
          disabled={isRoot}
        />
        <label
          htmlFor={`entity-${node.id}`}
          className="flex cursor-pointer items-center gap-2 text-sm font-medium"
        >
          <Building2 className="h-4 w-4 text-muted-foreground" />
          {node.name}
          {isRoot && (
            <span className="text-xs text-muted-foreground">(parent)</span>
          )}
          {node.memberCount > 0 && (
            <Badge variant="outline" className="ml-1 text-xs">
              {node.memberCount}
            </Badge>
          )}
        </label>
      </div>
      {node.children.map((child) => (
        <EntityCheckbox
          key={child.id}
          node={child}
          depth={depth + 1}
          excludedIds={excludedIds}
          onToggle={onToggle}
        />
      ))}
    </>
  );
}
